import React from "react";
import clsx from "clsx";

import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import RedCross from "~/components/Icons/icon-cross";
import CheckMark from "~/components/Icons/icon-check-mark";
import Circle from "~/components/Icons/icon-circle";
import translit from "translit-passport";
import IconRedAlert from "~/components/Icons/alert-triangle";
import IconYellowAlert from "~/components/Icons/alert-square";
import IconMinorAlert from "~/components/Icons/alert-circle";
import IconFile from "~/components/Icons/icon-file";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import s from "./TableRow.less";
import { LANG } from "~/constans/commom";
import { ViolationStatusType } from "~/schemas/generated/graphql";
import { dateDifIgnoreWeekends } from "~/libs/dateDifIgnoreWeekends";
import { ROLE_TYPES } from "~/constans/roles";
import { useUserRole } from "~/hooks/useUserRole";
import { format, getTime } from "date-fns";
import { usePermissions } from "~/shared/context/PermissionContext";

interface IProps {
  options: string[];
  btnEdit?: boolean;
  btnView?: boolean;
  btnVersion?: boolean;
  btnDel?: boolean;
  item;
}

function dateDifference(start, end) {
  const s = new Date(+start);
  const e = new Date(+end);
  s.setHours(12, 0, 0, 0);
  e.setHours(12, 0, 0, 0);
  // @ts-ignore
  const totalDays = Math.round((e - s) / 8.64e7);
  const wholeWeeks = (totalDays / 7) | 0;
  let days = wholeWeeks * 5;
  if (totalDays % 7) {
    s.setDate(s.getDate() + wholeWeeks * 7);
    while (s < e) {
      s.setDate(s.getDate() + 1);

      if (s.getDay() != 0 && s.getDay() != 6) {
        ++days;
      }
    }
  }
  return days;
}

const ViolationsTableRow: React.FC<IProps> = ({ options = [], item = {} }) => {
  const { hasPermission, loading: loadingPermissions } = usePermissions();
  const { t } = useTranslation();
  const history = useHistory();
  let lang = localStorage.getItem("language");
  let isEng = lang === LANG.en;
  const { sortField, page } = useGetLocationData();
  const setValue = useSetLocationData();

  const goToId = () => {
    history.push(`/violations/description/${item.id}`);
    setValue("page", page);
  };

  const deadline = getTime(new Date(item.dateTo));
  const createdAt = getTime(new Date(item.createdAt));
  const violationDone = item.answerStatus === "fixed";
  const workDaysToDeadline =
    dateDifIgnoreWeekends(new Date(), deadline) < 0
      ? 0
      : dateDifIgnoreWeekends(new Date(), deadline);
  const statusDeleted = item.answerStatus === ViolationStatusType.Deleted;

  const drawTd = (option) => {
    switch (option) {
      case "rationale":
        const { fileExist, link } = item[option];
        return (
          <div className={s.rationale}>
            {fileExist ? (
              <>
                <CheckMark fill={"#ec8c22"} />
                <a href={link} download onClick={(e) => e.stopPropagation()}>
                  <div className={s.downloadLink}>
                    <IconFile />
                  </div>
                </a>
              </>
            ) : (
              <RedCross fill={"#CE282B"} />
            )}
          </div>
        );
      case "answered":
        const comment =
          item["comments"].length > 0 ? item["comments"][0] : null;
        const commentRole = comment && comment.User.role;
        const isDealer = ROLE_TYPES.DEALER.name;
        const isHolding = ROLE_TYPES.HOLDING.name;
        const myRole = useUserRole();
        const iAmADealer = myRole === isDealer || myRole === isHolding;
        const Answered = () => (
          <div>
            <CheckMark width={"16px"} height={"16px"} fill={"#ec8c22"} />
          </div>
        );
        const NoAnswered = () => (
          <div>
            <Circle width={"16px"} height={"16px"} fill={"#89959c"} />
          </div>
        );
        const isDealerComment =
          commentRole &&
          (commentRole === isDealer || commentRole === isHolding);
        if (comment) {
          if (iAmADealer && isDealerComment) {
            return <NoAnswered />;
          }
          if (!iAmADealer && !isDealerComment) {
            return <NoAnswered />;
          }
          if (!iAmADealer && isDealerComment) {
            return <Answered />;
          }
          if (iAmADealer && !isDealerComment) {
            return <Answered />;
          }
        } else {
          return <NoAnswered />;
        }

        break;
      case "answerStatus":
        return (
          <div className={s.statusWrapper}>
            {item.punishment && <IconRedAlert />}
            <div className={s.status}>
              {t(`violationsTable.${item[option]}`)}
            </div>
          </div>
        );
      case "federalArea":
        return <div>{item[option][lang]}</div>;
      case "dealerName":
        return (
          <div className={s.dealerData}>
            <div>{item[option][lang].name}</div>
            <div>{item[option][lang].other}</div>
          </div>
        );
      case "deadline":
      case "DocDate":
        return (
          <div>
            {option === "DocDate"
              ? !item[option]
                ? ""
                : format(new Date(item[option]), "dd.MM.yyyy")
              : format(new Date(item[option]), "dd.MM.yyyy")}
          </div>
        );
      case "violationType":
        return (
          <div className={s.violationType}>
            {isEng ? translit.cyrillicToLatin(item[option]) : item[option]}
          </div>
        );
      case "DocNum":
      case "subdivision":
      case "reportedByUser":
        return <div>{item[option]}</div>;
      case "category":
        switch (item[option]) {
          case "critical":
            return (
              <span className={s.hover_text}>
                <IconRedAlert />
                <span>{t("violationsTable.critical")}</span>
              </span>
            );
          case "operational":
            return (
              <span className={s.hover_text}>
                <IconYellowAlert />
                <span>{t("violationsTable.operational")}</span>
              </span>
            );
          case "minor":
            return (
              <span className={s.hover_text}>
                <IconMinorAlert />
                <span>{t("violationsTable.minor")}</span>
              </span>
            );
        }
    }
  };

  return (
    <>
      <tr
        onClick={goToId}
        className={clsx(s.row_tr, {
          [s.normal]: workDaysToDeadline > 3 && !violationDone,
          [s.warning]:
            workDaysToDeadline <= 3 &&
            workDaysToDeadline !== 0 &&
            !violationDone &&
            !statusDeleted,
          [s.critical]:
            workDaysToDeadline <= 0 && !violationDone && !statusDeleted,
          [s.done]: violationDone,
          [s.deleted]: statusDeleted,
        })}
      >
        {options.map((option, id) => (
          <td
            key={id}
            className={clsx({
              [s.sorted]: sortField === option,
            })}
          >
            {drawTd(option)}
          </td>
        ))}
      </tr>
    </>
  );
};
export default ViolationsTableRow;
